import React, { useState } from "react";
import axios from "axios";
import './CourseForm.css'; // Import the CSS file for styling
import MultiSelect from "../MultiSelect/MultiSelect";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../Notification/NotificationContext";
import { useSelector } from "react-redux";
const token = localStorage.getItem("sassoontoken");

const CourseForm = () => {  
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const username = useSelector((state) => state.auth.user?.username);
  
  // Define location options based on user's region
  const locationOptions = username === "ukadmin" 
    ? ["Budapest", "Global", "London", "Manchester"]
    : ["Boston", "Beverly Hills", "McLean", "New York", "Scottsdale", "Toronto"];

  const [formData, setFormData] = useState({
    courseid: "",
    region: username === "ukadmin" ? "uk" : "us", // Set default region based on username
    category: [],
    title: "",
    duration: "",
    duration_unit: "",
    location: "",
    visibility: "show",
    currency: username === "ukadmin" ? "GBP" : "USD", // Default currency based on region
    visible_price: "",
    dates: [
      {
        date: "",
        price_with_vat: "",
        visibility: "",
        practice_option: "",
        places: "",
        booked_places: "",
        available_places: "",
        currency: "",
        deposit_amount: "",
        price_without_vat: "",
      },
    ],
  });

  const uniqueCategories = [
    "Barber", "Beginners", "Long Term", "Month of Mondays",
    "Short Term Colour", "Short Term Cut", "Short Term Cut & Colour",
    "Shows & Events", "Specialist"
  ];
  
  const categoryOptions = uniqueCategories.map((category) => ({
    value: category,
    label: category,
  }));
  
  // ✅ MultiSelect-specific handler
  const handleMultiSelectChange = (selectedOptions, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOptions,
    }));
    console.log("Selected options:", selectedOptions);
  };
  
  // Handle form field changes
  const handleChange = (e) => {
    console.log(formData, e.target.name);
    const { name, value } = e.target;
    const updatedFormData = { ...formData };
    if (name.includes("dates")) {
      const dateIndex = parseInt(name.split("-")[1]);
      const fieldName = name.split("-")[0];
      updatedFormData.dates[dateIndex][fieldName] = value;
    } else {
      updatedFormData[name] = value;
    }
    console.log(updatedFormData);
    setFormData(updatedFormData);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const finalFormData = {
        courseid: formData.courseid,
        region: formData.region,
        category: formData.category.map((category) => category.value),
        title: formData.title,
        duration: formData.duration,
        duration_unit: formData.duration_unit,
        location: formData.location,
        visibility: formData.visibility,
        currency: formData.currency,
        visible_price: formData.visible_price,
        dates: [
          {
            date: "",
            price_with_vat: "",
            visibility: "",
            practice_option: "",
            places: "",
            booked_places: "",
            available_places: "",
            currency: "",
            deposit_amount: "",
            price_without_vat: "",
          },
        ],
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/courses`,
        finalFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Attach token as Bearer token
          },
        }
      );

      console.log("Course added successfully:", response.data);
      
      showNotification("Course added successfully", "success");
      setTimeout(() => {
        navigate("/courses/places");
      }, 1500);

      // Reset form after successful submission
      setFormData({
        courseid: "",
        region: username === "ukadmin" ? "uk" : "us",
        category: [],
        title: "",
        duration: "",
        duration_unit: "",
        location: "",
        visibility: "show",
        currency: username === "ukadmin" ? "GBP" : "USD",
        visible_price: "",
        dates: [
          {
            date: "",
            price_with_vat: "",
            visibility: "",
            practice_option: "",
            places: "",
            booked_places: "",
            available_places: "",
            currency: "",
            deposit_amount: "",
            price_without_vat: "",
          },
        ],
      });
    } catch (error) {
      console.error("Error adding course:", error);
      showNotification("Error adding course, please try again.", "error");
    }
  };

  return (
    <div className="course-form-wrapper">
      <div className="back-button-container">
        <button
          className="back-button"
          onClick={() => navigate("/courses/places")}
        >
          <i className="bx bx-arrow-back"></i> Back to Course Places
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="form-container">
        <div className="modal-header">
          <h2>Add Course</h2>
        </div>

        <div className="form-grid">
          {/* Title at top full-width */}
          <div className="form-group full-width">
            <label htmlFor="title">Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          {/* Topic of Study and Location in two columns */}
          <div className="form-group">
            <label htmlFor="category">Topic of Study</label>
            <div className="multi-select-container">
              <MultiSelect
                options={categoryOptions}
                selectedValues={formData.category}
                onChange={(selected) => handleMultiSelectChange(selected, "category")}
                placeholder="Select categories..."
                showSearch={true}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="location">Location</label>
            <select
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
            >
              <option value="" disabled>Select an option</option>
              {locationOptions.map((location) => (
                <option key={location} value={location}>
                  {location}
                </option>
              ))}
            </select>
          </div>

          {/* Currency and Visible Price row */}
          <div className="form-group">
            <label htmlFor="currency">Currency</label>
            <select
              id="currency"
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              required
            >
              {username === "ukadmin" ? (
                <option value="GBP">GBP</option>
              ) : (
                <>
                  <option value="CAD">CAD</option>
                  <option value="USD">USD</option>
                </>
              )}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="visible_price">Visible Price</label>
            <input
              type="number"
              name="visible_price"
              value={formData.visible_price}
              onChange={handleChange}
              required
            />
          </div>

          {/* Duration and Duration Unit */}
          <div className="form-group">
            <label htmlFor="duration">Duration</label>
            <input
              type="number"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="duration_unit">Duration Unit</label>
            <select
              id="duration_unit"
              name="duration_unit"
              value={formData.duration_unit}
              onChange={handleChange}
              required
            >
              <option value="" disabled>Select an option</option>
              <option value="Consecutive Mondays">Consecutive Mondays</option>
              <option value="Day">Day</option>
              <option value="Days">Days</option>
              <option value="Hours per session">Hours per session</option>
              <option value="Minutes">Minutes</option>
              <option value="Weeks">Weeks</option>
            </select>
          </div>        

          {/* Salesforce Course ID and Stripe Account at the end */}
          <div className="form-group">
            <label htmlFor="courseid">Salesforce Course ID</label>
            <input
              type="text"
              name="courseid"
              value={formData.courseid}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="region">Stripe Account</label>
            <select
              id="region"
              name="region"
              value={formData.region}
              onChange={handleChange}
              required
              disabled // Disable region selection as it's determined by user type
            >
              <option value={username === "ukadmin" ? "uk" : "us"}>
                {username === "ukadmin" ? "VS Salons (UK)" : "Sassoon Academy NA (US)"}
              </option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="visibility">Visibility</label>
            <select
              id="visibility"
              name="visibility"
              value={formData.visibility}
              onChange={handleChange}
              required
            >
              <option value="show">Show</option>
              <option value="hide">Hide</option>
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit">Add Course</button>
        </div>
      </form>
    </div>
  );
};

export default CourseForm;
